import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  ReactNode,
  FC
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { ReferentialKey } from 'api/referential';
import { notesAPI } from 'services/swagger-api';
import { getMultipleReferentialsByKeys } from 'features/referentials/slices/getMultipleReferentialsByKeysThunk';
import { selectReferentialDataByKey } from 'features/referentials/selectors/selectReferentialDataByKey';
import { FilterPeriodDto, SortingFieldEnum } from 'api/note';
import { toDayInput } from 'core/utils/dates';
import {
  NoteListItemDto,
  NoteFormValues,
  NoteFilters,
  DateSort,
  NoteEntityContext
} from '../types/interface';
import {
  mapApiNotesToUiNotes,
  mapFormValuesToCreateCommand,
  mapFormValuesToUpdateCommand,
  mapApiNoteToUiNote
} from '../mappers/noteMappers';

// ============================================================================
// Types
// ============================================================================

interface NoteState {
  notes: NoteListItemDto[];
  totalCount: number;
  selectedNote: NoteListItemDto | null;
  isLoading: boolean;
  error: string | null;
  isAddingNote: boolean;
  isEditing: boolean;
  filters: NoteFilters;
  entityContext: NoteEntityContext | null;
}

interface FetchNotesOptions {
  selectNoteId?: string;
  forceSelectFirst?: boolean;
}

interface NoteContextValue extends NoteState {
  // CRUD
  fetchNotes: (options?: FetchNotesOptions) => Promise<void>;
  createNote: (values: NoteFormValues) => Promise<void>;
  updateNote: (id: string, values: NoteFormValues) => Promise<void>;
  deleteNote: (id: string) => Promise<void>;

  // Selection
  selectNote: (note: NoteListItemDto | null) => Promise<void>;

  // Modes
  setAddingMode: (isAdding: boolean) => void;
  setEditingMode: (isEditing: boolean) => void;

  // Filters
  setSearchFilter: (search: string) => void;
  setPriorityFilter: (priorityCode: string | 'all') => void;
  setDateSortFilter: (sort: DateSort) => void;
}

// ============================================================================
// Context
// ============================================================================

const NoteContext = createContext<NoteContextValue | null>(null);

// ============================================================================
// Provider
// ============================================================================

interface NoteProviderProps {
  children: ReactNode;
  riskId?: string;
  personId?: string;
}

export const NoteProvider: FC<NoteProviderProps> = ({
  children,
  riskId,
  personId
}) => {
  const dispatch = useAppDispatch();

  // Get priorities from referential store
  const prioritiesData = useAppSelector(
    selectReferentialDataByKey(ReferentialKey.NotePriority)
  );

  // Entity context
  const entityContext = useMemo<NoteEntityContext | null>(() => {
    if (riskId || personId) {
      return { riskId, personId };
    }
    return null;
  }, [riskId, personId]);

  // State
  const [notes, setNotes] = useState<NoteListItemDto[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedNote, setSelectedNote] = useState<NoteListItemDto | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [filters, setFilters] = useState<NoteFilters>({
    search: '',
    priorityCode: 'all',
    dateSort: 'week'
  });

  // ============================================================================
  // Load referentials on mount
  // ============================================================================

  useEffect(() => {
    dispatch(
      getMultipleReferentialsByKeys([
        ReferentialKey.NotePriority,
        ReferentialKey.NoteStatus,
        ReferentialKey.NoteTag
      ])
    );
  }, [dispatch]);

  // ============================================================================
  // Build period from dateSort filter
  // ============================================================================

  const buildPeriod = useCallback((dateSort: DateSort): FilterPeriodDto => {
    const now = new Date();
    const startDate = new Date(now);
    if (dateSort === 'month') {
      startDate.setMonth(now.getMonth() - 1);
    } else {
      startDate.setDate(now.getDate() - 7);
    }

    // Dates CALENDAIRES (contrat back « Dates & fuseaux ») : bornes en "yyyy-MM-dd"
    // du fuseau local (toDayInput = getters locaux), jamais de Date sérialisé.
    return { startDate: toDayInput(startDate), endDate: toDayInput(now) };
  }, []);

  // ============================================================================
  // CRUD Actions
  // ============================================================================

  const fetchNotes = useCallback(
    async (options?: { selectNoteId?: string; forceSelectFirst?: boolean }) => {
      setIsLoading(true);
      setError(null);
      try {
        // Build filters based on entity context and filter state
        const buildFilters = () => {
          const filtersObj: {
            riskIds?: string[];
            personIds?: string[];
            period?: FilterPeriodDto;
            priorities?: string[];
          } = {};

          // Add entity context filters
          if (entityContext?.riskId) {
            filtersObj.riskIds = [entityContext.riskId];
          }
          if (entityContext?.personId) {
            filtersObj.personIds = [entityContext.personId];
          }

          // Add period filter based on dateSort
          filtersObj.period = buildPeriod(filters.dateSort);

          // Add priorities filter from referential store (all priority IDs)
          if (prioritiesData && prioritiesData.length > 0) {
            // If a specific priority is selected, filter by that one
            if (filters.priorityCode !== 'all') {
              const selectedPriority = prioritiesData.find(
                (p) => p.code === filters.priorityCode
              );
              if (selectedPriority?.id) {
                filtersObj.priorities = [selectedPriority.id];
              }
            }
          }

          return Object.keys(filtersObj).length > 0 ? filtersObj : undefined;
        };

        const response = await notesAPI.getListeNotesAsync({
          getGenericListCommandDto: {
            pageNumber: 1,
            pageSize: 50,
            searchString: filters.search || undefined,
            sorting: SortingFieldEnum.CreationTimeDesc as any,
            filters: buildFilters()
          }
        });

        const mappedNotes = mapApiNotesToUiNotes(response.items || []);
        setNotes(mappedNotes);
        setTotalCount(response.totalCount || 0);

        // Handle note selection after fetch
        const shouldSelectFirst =
          options?.forceSelectFirst || mappedNotes.length > 0;

        if (options?.selectNoteId) {
          // Select a specific note by ID (e.g., after create/update)
          const noteToSelect = mappedNotes.find(
            (n) => n.id === options.selectNoteId
          );
          if (noteToSelect?.id) {
            setSelectedNote(noteToSelect);
            try {
              const noteResponse = await notesAPI.getNoteAsync({
                noteId: noteToSelect.id
              });
              const fullNote = mapApiNoteToUiNote(noteResponse);
              setSelectedNote(fullNote);
            } catch (err) {
              console.error('Error fetching note details:', err);
            }
          } else if (shouldSelectFirst && mappedNotes.length > 0) {
            // Note not found in list, select first
            await selectFirstNote(mappedNotes);
          } else {
            setSelectedNote(null);
          }
        } else if (options?.forceSelectFirst && mappedNotes.length > 0) {
          // Force select first note (e.g., after delete)
          await selectFirstNote(mappedNotes);
        } else if (mappedNotes.length === 0) {
          setSelectedNote(null);
        }
      } catch (err) {
        setError('Erreur lors du chargement des notes');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    },
    [
      filters.search,
      filters.dateSort,
      filters.priorityCode,
      buildPeriod,
      prioritiesData,
      entityContext
    ]
  );

  // Helper to select first note with full details
  const selectFirstNote = async (notes: NoteListItemDto[]) => {
    const firstNote = notes[0];
    setSelectedNote(firstNote);
    if (firstNote?.id) {
      try {
        const noteResponse = await notesAPI.getNoteAsync({
          noteId: firstNote.id
        });
        const fullNote = mapApiNoteToUiNote(noteResponse);
        setSelectedNote(fullNote);
      } catch (err) {
        console.error('Error fetching note details:', err);
      }
    }
  };

  // Deep-link : /mes-notes?noteId=xxx (ex. clic sur une note dans la recherche globale)
  // → sélectionne cette note au premier chargement, consommé une seule fois.
  const [searchParams] = useSearchParams();
  const deepLinkNoteIdRef = useRef<string | null>(searchParams.get('noteId'));

  // Fetch notes on mount and when filters change
  useEffect(() => {
    const deepLinkNoteId = deepLinkNoteIdRef.current;
    deepLinkNoteIdRef.current = null;
    fetchNotes(deepLinkNoteId ? { selectNoteId: deepLinkNoteId } : undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search, filters.dateSort, filters.priorityCode, entityContext]);

  const createNote = useCallback(
    async (values: NoteFormValues) => {
      setIsLoading(true);
      setError(null);
      try {
        const command = mapFormValuesToCreateCommand(
          values,
          entityContext?.riskId,
          entityContext?.personId
        );

        const createdNote = await notesAPI.createNoteAsync({
          createNoteCommand: command
        });

        // Refresh the list and select the newly created note
        await fetchNotes({
          selectNoteId: createdNote?.id,
          forceSelectFirst: true
        });
        setIsAddingNote(false);
      } catch (err) {
        setError('Erreur lors de la création de la note');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    },
    [entityContext, fetchNotes]
  );

  const updateNote = useCallback(
    async (id: string, values: NoteFormValues) => {
      setIsLoading(true);
      setError(null);
      try {
        const command = mapFormValuesToUpdateCommand(
          values,
          entityContext?.riskId,
          entityContext?.personId
        );

        await notesAPI.updateNoteAsync({
          noteId: id,
          updateNoteCommand: command
        });

        // Refresh the list and re-select the updated note
        // This ensures the list reflects any changes that might affect filtering
        await fetchNotes({ selectNoteId: id });
        setIsEditing(false);
      } catch (err) {
        setError('Erreur lors de la mise à jour de la note');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    },
    [entityContext, fetchNotes]
  );

  const deleteNote = useCallback(
    async (id: string) => {
      setIsLoading(true);
      setError(null);
      try {
        await notesAPI.deleteNoteAsync({ noteId: id });

        // Refresh the list and select the first remaining note
        await fetchNotes({ forceSelectFirst: true });
        setIsEditing(false);
      } catch (err) {
        setError('Erreur lors de la suppression de la note');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    },
    [fetchNotes]
  );

  // ============================================================================
  // Selection
  // ============================================================================

  const selectNote = useCallback(async (note: NoteListItemDto | null) => {
    setIsAddingNote(false);
    setIsEditing(false);

    if (!note?.id) {
      setSelectedNote(null);
      return;
    }

    // Set immediately for responsiveness
    setSelectedNote(note);

    // Fetch full note details to get person and contract info
    try {
      const noteResponse = await notesAPI.getNoteAsync({ noteId: note.id });
      const fullNote = mapApiNoteToUiNote(noteResponse);
      setSelectedNote(fullNote);
    } catch (err) {
      console.error('Error fetching note details:', err);
      // Keep the list item version if fetch fails
    }
  }, []);

  // ============================================================================
  // Modes
  // ============================================================================

  const setAddingMode = useCallback((isAdding: boolean) => {
    setIsAddingNote(isAdding);
    if (isAdding) {
      setSelectedNote(null);
      setIsEditing(false);
    }
  }, []);

  const setEditingMode = useCallback((editing: boolean) => {
    setIsEditing(editing);
  }, []);

  // ============================================================================
  // Filters
  // ============================================================================

  const setSearchFilter = useCallback((search: string) => {
    setFilters((prev) => ({ ...prev, search }));
  }, []);

  const setPriorityFilter = useCallback((priorityCode: string | 'all') => {
    setFilters((prev) => ({ ...prev, priorityCode }));
  }, []);

  const setDateSortFilter = useCallback((dateSort: DateSort) => {
    setFilters((prev) => ({ ...prev, dateSort }));
  }, []);

  // ============================================================================
  // Context Value
  // ============================================================================

  const value = useMemo<NoteContextValue>(
    () => ({
      // State
      notes,
      totalCount,
      selectedNote,
      isLoading,
      error,
      isAddingNote,
      isEditing,
      filters,
      entityContext,

      // CRUD
      fetchNotes,
      createNote,
      updateNote,
      deleteNote,

      // Selection
      selectNote,

      // Modes
      setAddingMode,
      setEditingMode,

      // Filters
      setSearchFilter,
      setPriorityFilter,
      setDateSortFilter
    }),
    [
      notes,
      totalCount,
      selectedNote,
      isLoading,
      error,
      isAddingNote,
      isEditing,
      filters,
      entityContext,
      fetchNotes,
      createNote,
      updateNote,
      deleteNote,
      selectNote,
      setAddingMode,
      setEditingMode,
      setSearchFilter,
      setPriorityFilter,
      setDateSortFilter
    ]
  );

  return <NoteContext.Provider value={value}>{children}</NoteContext.Provider>;
};

// ============================================================================
// Hook
// ============================================================================

export const useNote = (): NoteContextValue => {
  const context = useContext(NoteContext);
  if (!context) {
    throw new Error('useNote must be used within a NoteProvider');
  }
  return context;
};
